import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router';
import { submissionsApi } from '@/api/submissions';
import { queryKeys } from '@/lib/queryKeys';
import { formatDateTime } from '@/lib/formatDate';
import { ArrowLeft, Trash2 } from 'lucide-react';

export function SubmissionDetailPage() {
  const { formId, id } = useParams<{ formId: string; id: string }>();

  const navigate = useNavigate();
  const qc = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => submissionsApi.delete(formId!, id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.submissions.list(formId!, {}) });
      navigate(`/forms/${formId}`);
    },
  });

  const { data } = useQuery({
    queryKey: queryKeys.submissions.detail(formId!, id!),
    queryFn: () => submissionsApi.get(formId!, id!),
    enabled: !!formId && !!id,
  });

  const sub = data?.submission;
  if (!sub) return <div className="animate-pulse"><div className="h-8 bg-gray-200 rounded w-1/3" /></div>;

  return (
    <div className="max-w-3xl">
      <Link to={`/forms/${formId}`} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="h-4 w-4" /> Voltar ao formulario
      </Link>

      <h1 className="text-2xl font-bold mb-6">Detalhe do Envio</h1>

      <div className="bg-white rounded-xl border border-gray-200 mb-6">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold">Dados</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {Object.entries(sub.data || {}).map(([key, value]) => (
            <div key={key} className="flex px-5 py-3">
              <span className="w-48 shrink-0 font-medium text-gray-700 text-sm">{key}</span>
              <span className="text-sm text-gray-600 whitespace-pre-wrap break-all">{String(value)}</span>
            </div>
          ))}
          {Object.keys(sub.data || {}).length === 0 && (
            <div className="px-5 py-3 text-sm text-gray-400">Sem dados</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold">Metadados</h2>
        </div>
        <div className="divide-y divide-gray-100">
          <div className="flex px-5 py-3">
            <span className="w-48 shrink-0 font-medium text-gray-700 text-sm">Enviado em</span>
            <span className="text-sm text-gray-600">{formatDateTime(sub.metadata.submittedAt)}</span>
          </div>
          <div className="flex px-5 py-3">
            <span className="w-48 shrink-0 font-medium text-gray-700 text-sm">Endereco IP</span>
            <span className="text-sm text-gray-600">{sub.metadata.ipAddress}</span>
          </div>
          <div className="flex px-5 py-3">
            <span className="w-48 shrink-0 font-medium text-gray-700 text-sm">Navegador</span>
            <span className="text-sm text-gray-600 break-all">{sub.metadata.userAgent}</span>
          </div>
          {sub.metadata.referrer && (
            <div className="flex px-5 py-3">
              <span className="w-48 shrink-0 font-medium text-gray-700 text-sm">Referencia</span>
              <span className="text-sm text-gray-600">{sub.metadata.referrer}</span>
            </div>
          )}
          <div className="flex px-5 py-3">
            <span className="w-48 shrink-0 font-medium text-gray-700 text-sm">Lido</span>
            <span className="text-sm text-gray-600">{sub.isRead ? 'Sim' : 'Nao'}</span>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <button
          onClick={() => { if (confirm('Excluir este envio?')) deleteMutation.mutate(); }}
          disabled={deleteMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
          {deleteMutation.isPending ? 'Excluindo...' : 'Excluir Envio'}
        </button>
      </div>
    </div>
  );
}
